import { Dialog } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import type { ReactNode } from "react";

export const HoldCleaningWorkflowModal = ({
  open,
  title,
  description,
  busy,
  error,
  submitLabel,
  onClose,
  onSubmit,
  children,
}: {
  open: boolean;
  title: string;
  description: string;
  busy: boolean;
  error: string | null;
  submitLabel: string;
  onClose: () => void;
  onSubmit: () => void;
  children: ReactNode;
}) => (
  <Dialog open={open} onClose={() => !busy && onClose()} className="relative z-50">
    <div className="fixed inset-0 bg-black/45" aria-hidden="true" />
    <div className="fixed inset-0 overflow-y-auto px-4 py-8">
      <div className="flex min-h-full items-center justify-center">
        <Dialog.Panel className="w-full max-w-4xl rounded-xl bg-white text-[#292F2D] shadow-2xl dark:bg-gray-800 dark:text-gray-100">
          <header className="flex items-start justify-between border-b border-[#DFE4E2] px-6 py-5 dark:border-gray-700">
            <div>
              <Dialog.Title className="text-xl font-semibold text-[#292F2D] dark:text-gray-100">{title}</Dialog.Title>
              <Dialog.Description className="mt-1 text-sm text-[#4A5655] dark:text-gray-300">
                {description}
              </Dialog.Description>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="rounded-lg p-2 hover:bg-[#F3F4F4] dark:hover:bg-gray-900"
            >
              <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              <span className="sr-only">Close</span>
            </button>
          </header>
          <div className="max-h-[68vh] overflow-y-auto px-6 py-5">{children}</div>
          {error && <p className="mx-6 mb-3 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">{error}</p>}
          <footer className="flex justify-end gap-3 border-t border-[#DFE4E2] px-6 py-4 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="rounded-lg border border-[#C7D0CE] px-4 py-2 text-sm font-semibold dark:border-gray-600"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onSubmit}
              disabled={busy}
              className="rounded-lg bg-[#1F8475] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              {busy ? "Saving..." : submitLabel}
            </button>
          </footer>
        </Dialog.Panel>
      </div>
    </div>
  </Dialog>
);

export const workflowInputClass =
  "mt-1 w-full rounded-lg border border-[#C7D0CE] bg-white text-[#292F2D] dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 px-3 py-2 text-sm outline-none focus:border-[#1F8475] focus:ring-1 focus:ring-[#1F8475]";

export const WorkflowField = ({ label, children }: { label: string; children: ReactNode }) => (
  <label className="block text-xs font-semibold uppercase tracking-wide text-[#4A5655] dark:text-gray-300">
    {label}
    {children}
  </label>
);
